import { ActionTree } from 'vuex';
import { ISelectedPageFieldIds } from '../types/selected-page-field-ids';
import { EFieldAction, EFieldMutation, state as defaultState } from './field.constants';
import { IFieldState } from './field.types';
import { FieldService } from '@/api/fields/services/field.service';
import { EAlignFieldType } from '@/dealroom/fields/context-menu/types/enums/align-field-type';
import { FieldPlacementHelper } from '@/dealroom/fields/helpers/field-placement.helper';
import { FieldHelper } from '@/dealroom/fields/helpers/field.helper';
import { EFieldType } from '@/dealroom/fields/types/enums/field-type';
import { IField } from '@/dealroom/fields/types/field';
import { IFieldPosition } from '@/dealroom/fields/types/field-position';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { namespace as alertNamespace } from '@/store/stores/alert.store';
import { IDocumentPage } from '@/types/document-page';

export const actions: ActionTree<IFieldState, RootState> = {
  async [EFieldAction.Load]({ dispatch, commit }, documentId: string) {
    commit(EFieldMutation.SetDocumentId, documentId);
    commit(EFieldMutation.SetLoading, true);
    try {
      const fields: IField[] = await FieldService.load(documentId);
      const mappedFields: IField[] = fields.map((field: IField) =>
        field.type === EFieldType.Merge
          ? {
              ...field,
              displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
            }
          : field
      );
      dispatch(EFieldAction.LoadSuccess, mappedFields);
    } catch (err) {
      dispatch(EFieldAction.LoadFailed);
    }
  },
  [EFieldAction.LoadSuccess]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Set, fields);
    commit(EFieldMutation.SetLoading, false);
  },
  [EFieldAction.LoadFailed]({ commit }) {
    commit(EFieldMutation.SetLoading, false);
  },
  [EFieldAction.Add]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Add, fields);
  },
  [EFieldAction.Remove]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Remove, fields);
  },
  [EFieldAction.ReplaceCreatedField](
    { commit },
    { tempId, field }: { tempId: string; field: IField }
  ) {
    commit(EFieldMutation.ReplaceFieldWithTempId, {
      field,
      tempId,
    });
  },
  async [EFieldAction.Update]({ commit, state, dispatch }, fields: IField[]) {
    const previousFields: IField[] = state.fields;
    const updatedFields: IField[] = previousFields.map((stateField: IField) =>
      fields.some((field: IField) => field.id === stateField.id)
        ? fields.find((field: IField) => field.id === stateField.id)
        : stateField
    );
    dispatch(EFieldAction.SetLastUpdatedFieldId, fields);
    commit(EFieldMutation.Set, updatedFields);
    try {
      await FieldService.updateMultiple(fields);
    } catch {
      dispatch(EFieldAction.UpdateFailed, previousFields);
    }
  },
  [EFieldAction.UpdateFailed]({ commit, dispatch }, previousField: IField) {
    commit(EFieldMutation.Update, previousField);
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-update-field'), {
      root: true,
    });
  },
  [EFieldAction.Select]({ commit }, field: IField) {
    commit(EFieldMutation.Select, field);
  },
  [EFieldAction.Deselect]({ commit }, field: IField) {
    commit(EFieldMutation.Deselect, field);
  },
  [EFieldAction.Tab]({ commit, state }, pages: IDocumentPage[]) {
    const { fieldIds } = state.selectedPageFieldIds;
    const nextField: IField = FieldHelper.getNextTabField(state.fields, fieldIds, pages);
    commit(EFieldMutation.SetSelected, [nextField]);
    commit(EFieldMutation.SetTabFieldId, nextField.id);
  },
  [EFieldAction.DeselectAll]({ commit }) {
    commit(EFieldMutation.DeselectAll);
  },
  async [EFieldAction.Duplicate]({ commit, dispatch, state }) {
    try {
      const fieldsToCopy: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((field: IField) => {
          const fieldToCopy: IField = { ...field };
          delete fieldToCopy.id;
          const duplicatePosition: IFieldPosition = FieldHelper.getDuplicateFieldPosition(field);

          return {
            ...fieldToCopy,
            position: FieldPlacementHelper.correctFieldPosition(
              duplicatePosition,
              field.pageId,
              state.pageWidth
            ),
          };
        });
      const copiedFields: IField[] = await FieldService.createMultiple(fieldsToCopy);
      commit(EFieldMutation.Add, copiedFields);
      commit(EFieldMutation.SetSelected, copiedFields);
      dispatch(EFieldAction.SetLastUpdatedFieldId, copiedFields);
    } catch (e) {
      dispatch(EFieldAction.DuplicateFailed);
    }
  },
  [EFieldAction.DuplicateFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-copy-field'), {
      root: true,
    });
  },
  [EFieldAction.UpdatePageWidth]({ commit }, pageWidth: number) {
    commit(EFieldMutation.SetPageWidth, pageWidth);
  },
  [EFieldAction.Align]({ state, dispatch }, actionType: EAlignFieldType) {
    const fields: IField[] = state.fields.filter((field: IField) =>
      state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
    );
    try {
      const fieldContainerPosition: IFieldPosition = FieldPlacementHelper.calculateFieldContainerPosition(
        fields
      );
      const updatedFields: IField[] = FieldHelper.getAlignedFields(
        fields,
        actionType,
        fieldContainerPosition
      );
      dispatch(EFieldAction.Update, updatedFields);
    } catch (e) {
      dispatch(EFieldAction.AlignFailed);
    }
  },
  [EFieldAction.AlignFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-align-fields'), {
      root: true,
    });
  },
  [EFieldAction.Move]({ dispatch, state }, pageId: string) {
    const movedFields: IField[] = state.fields
      .filter((field: IField) =>
        state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
      )
      .map((field: IField) => ({
        ...field,
        pageId,
        position: FieldPlacementHelper.correctFieldPosition(
          field.position,
          pageId,
          state.pageWidth
        ),
      }));

    dispatch(EFieldAction.Update, movedFields);
    dispatch(EFieldAction.DeselectAll);
  },
  [EFieldAction.ChangeTextSize]({ dispatch, state }, height: number) {
    try {
      const updatedFields: IField[] = state.fields
        .filter((field: IField) =>
          state.selectedPageFieldIds.fieldIds.find((id: string) => id === field.id)
        )
        .map((selectedField: IField) => {
          if (selectedField.position.height !== height && selectedField.type !== EFieldType.Check) {
            selectedField.position.height = height;
          }
          return selectedField;
        });
      dispatch(EFieldAction.Update, updatedFields);
    } catch (e) {
      dispatch(EFieldAction.ChangeTextSizeFailed);
    }
  },
  [EFieldAction.ChangeTextSizeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-change-field-size'), {
      root: true,
    });
  },
  [EFieldAction.Focus]({ commit }, fieldId: string) {
    commit(EFieldMutation.SetFocusedFieldId, fieldId);
  },
  async [EFieldAction.LoadSenderSignature]({ dispatch }, userId: string) {
    try {
      const signatureData: string = await FieldService.loadUserSignature(userId);
      dispatch(EFieldAction.LoadSenderSignatureSuccess, signatureData);
    } catch (e) {
      dispatch(EFieldAction.LoadSenderSignatureFailed);
    }
  },
  [EFieldAction.LoadSenderSignatureSuccess]({ commit }, signatureData: string) {
    commit(EFieldMutation.SetSenderSignature, signatureData);
  },
  [EFieldAction.LoadSenderSignatureFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDangerAlert`, translate('could-not-load-signature'), {
      root: true,
    });
  },
  [EFieldAction.SetLastUpdatedFieldId]({ commit }, updatedFields: IField[]) {
    const sortedUpdatedFields: IField[] = FieldHelper.sortByPositionWithoutPages(updatedFields);
    const lastUpdatedField: IField = sortedUpdatedFields[sortedUpdatedFields.length - 1];
    commit(EFieldMutation.SetLastUpdatedFieldId, lastUpdatedField.id);
  },
  [EFieldAction.SetSelectBox]({ commit }, selectedPageFieldIds: ISelectedPageFieldIds) {
    commit(EFieldMutation.SetSelectBox, selectedPageFieldIds);
  },
  [EFieldAction.EndSelectBox]({ commit, state }) {
    commit(EFieldMutation.SetSelectedPageFieldIds, state.markedPageFieldIds);
    commit(EFieldMutation.SetSelectBox, defaultState.markedPageFieldIds);
  },
};
